import type { ReactNode } from 'react'
import { useOrganizationStore } from '@/store/organization.store'
import { useLocation, useParams } from 'react-router-dom'

export interface TabItem {
  label: string
  path: string
  disabled?: boolean
  id?: string
  isActive?: (currentPath: string) => boolean
  /**
   * In-page tab: selecting it flips local state instead of navigating. When
   * set, PageTabs renders a button (no Link) and `isActive` decides the
   * active state — the path is ignored.
   */
  onSelect?: () => void
  count?: number
  badge?: 'beta' | 'coming-soon' | 'new'
  icon?: ReactNode
  prominent?: boolean
}

export interface ModuleNavigationOptions {
  moduleSlug: string
  tabs: TabItem[]
  isDetailView?: boolean
  entityId?: string
  entityParamName?: string // New prop to specify the URL param name to look for
}

export function useModuleNavigation({
  moduleSlug,
  tabs,
  isDetailView = false,
  entityId: explicitEntityId,
  entityParamName,
}: ModuleNavigationOptions) {
  const { currentOrganization } = useOrganizationStore()
  const params = useParams()
  const location = useLocation()

  // Get entity ID with prioritized approach:
  // 1. Use explicitly provided entityId if available
  // 2. Use entityParamName to look up in params if specified
  // 3. Use URL pattern /{module}/{id}/* as fallback for detail views
  const entityId =
    explicitEntityId ||
    (entityParamName && params[entityParamName]) ||
    (isDetailView && location.pathname.split('/').filter(Boolean)[2])

  // Helper to generate path
  const generatePath = (tabPath: string) => {
    const basePath = `/${currentOrganization?.slug}/${moduleSlug}`

    if (isDetailView && entityId) {
      if (tabPath === '') {
        return `${basePath}/${entityId}`
      } else {
        return `${basePath}/${entityId}${tabPath}`
      }
    } else {
      return tabPath === '' ? basePath : `${basePath}${tabPath}`
    }
  }

  // Generate paths with organization slug
  const moduleTabs = tabs.map(tab => {
    const finalPath = generatePath(tab.path)

    return {
      ...tab,
      path: finalPath,
      id: tab.id || tab.path.replace('/', ''),
    }
  })

  return { moduleTabs, entityId }
}
